import React, { useState, useMemo } from 'react';
import moment from 'moment';

import { SpinnerWrapper } from 'components/Spinner/Spinner';
import { ButtonsContainer, FreecodeButton, RectangleButtonLink } from 'components/Button/RectangleButton';
import { ButtonIcon } from 'components/Button/styles';
import { useFreecodeCreatorContext, useFreecodeContextData } from 'hooks/useContext';
import CreateWipTestImplModal from './Modals/CreateWipTestImplModal';
import MakeWipTestImplModal from './Modals/MakeWipTestImplModal';
import RevertWipTestImplModal from './Modals/RevertWipTestImplModal';
import { ImplVersionContainer, VersionStatus } from './styles';
import { CreatorSection, SectionHeader, Label, Text } from '../styles';

export default function FreecodeVersioning() {
  const { test, testImpl } = useFreecodeContextData();
  const { implVersions } = useFreecodeCreatorContext();

  const loading = useMemo(() => !implVersions?.length, [implVersions]);
  const hasWip = useMemo(() => implVersions?.every?.((value) => !value.isWIP), [implVersions]);

  const [modalsStatus, setModalsStatus] = useState({
    createWipTestImplModal: false,
    makeWipTestImplModal: false,
    revertWipTestImplModal: false,
  });

  // Display existing test implementation versions
  const renderExistingVersions = () => (
    <>
      <Label>Existing versions</Label>
      {implVersions.map((implVersion, index) => {
        return (
          <ImplVersionContainer key={index}>
            {implVersion._id !== testImpl._id && (
              <RectangleButtonLink
                small
                floatRight
                href={`/freecode-creator/${test._id}?testImplId=${implVersion._id}`}
              >
                Open
              </RectangleButtonLink>
            )}
            <Label displayBlock>
              {implVersion.isLive && <VersionStatus color="green">Live </VersionStatus>}
              {implVersion.isWIP && <VersionStatus color="orange">Work in progress </VersionStatus>}
              {!implVersion.isLive && !implVersion.isWIP && <VersionStatus color="red">Old </VersionStatus>}
              version, executor {implVersion.executionVersion}
            </Label>
            <Text>{implVersion._id}</Text>
            <Text>
              Created {moment(implVersion.createdAt).format('YYYY-MM-DD')}, Edited{' '}
              {moment(implVersion.editedAt).format('YYYY-MM-DD')}
            </Text>
          </ImplVersionContainer>
        );
      })}
    </>
  );

  return (
    <CreatorSection>
      <SectionHeader>Versioning</SectionHeader>
      <Text>
        Handle versions of the test implementation. There can only be one work in progress version and new versions are
        created from the live version.
      </Text>
      <SpinnerWrapper loading={loading}>
        <ButtonsContainer>
          {hasWip && (
            <FreecodeButton
              small
              isFreecodeButton
              title="Create a new Work in Progress implementation"
              onClick={() => setModalsStatus({ ...modalsStatus, createWipTestImplModal: true })}
            >
              <ButtonIcon icon="fa fa-plus" />
              Create WIP
            </FreecodeButton>
          )}
          {!hasWip && (
            <>
              <FreecodeButton
                small
                isFreecodeButton
                title="Make current WIP the new live implementation"
                onClick={() => setModalsStatus({ ...modalsStatus, makeWipTestImplModal: true })}
              >
                Make WIP live
              </FreecodeButton>
              <FreecodeButton
                small
                isFreecodeButton
                marginLeft
                title="Delete the current WIP"
                onClick={() => setModalsStatus({ ...modalsStatus, revertWipTestImplModal: true })}
              >
                Revert WIP
              </FreecodeButton>
            </>
          )}
        </ButtonsContainer>
        {implVersions && renderExistingVersions()}
      </SpinnerWrapper>
      {modalsStatus.createWipTestImplModal && (
        <CreateWipTestImplModal
          testId={test._id}
          closeModal={() => setModalsStatus({ ...modalsStatus, createWipTestImplModal: false })}
        />
      )}
      {modalsStatus.makeWipTestImplModal && (
        <MakeWipTestImplModal
          testId={test._id}
          closeModal={() => setModalsStatus({ ...modalsStatus, makeWipTestImplModal: false })}
        />
      )}
      {modalsStatus.revertWipTestImplModal && (
        <RevertWipTestImplModal
          testId={test._id}
          closeModal={() => setModalsStatus({ ...modalsStatus, revertWipTestImplModal: false })}
        />
      )}
    </CreatorSection>
  );
}
